import React, { useState, useEffect } from 'react';
import { db, auth, getCollectionName } from './firebase';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, orderBy, getDocs, getDoc } from './firestore-compat';
import { uploadArquivo, ArquivoLink } from './arquivos';
import { toTitleCase } from './utils/formatters';

const CONSELHOS = ["CRM", "COREN", "CRN", "CRP", "CREFITO", "CREFONO", "CRO", "CFESS"];

const getLiberacaoStatus = (prof) => {
  if (!prof?.liberacaoTemporaria) return null;
  if (prof.statusRH === 'Aprovado' && prof.statusTI === 'Criado') return null;
  const grantedAt = new Date(prof.liberacaoTemporariaEm);
  if (isNaN(grantedAt.getTime())) return { ativa: true, diasRestantes: 30 };
  const expiresAt = new Date(grantedAt.getTime() + 30 * 24 * 60 * 60 * 1000);
  const now = new Date();
  const diasRestantes = Math.ceil((expiresAt - now) / 86400000);
  return { ativa: now < expiresAt, diasRestantes, expiresAt };
};
const tiposProfissional_DEFAULT = ["TÉCNICO DE ENFERMAGEM", "ENFERMEIRO", "FISIOTERAPEUTA", "FONOAUDIOLOGO", "NUTRICIONISTA", "MEDICO", "PSICOLOGO", "ASSISTENTE SOCIAL", "ODONTOLOGISTA"];

const RESET_STATUS = {
  Pendente: "⏳ Pendente",
  Finalizado: "✅ Finalizado",
  Recusado: "❌ Recusado"
};

const DOC_TYPES = {
  carteiraConselho: "Carteira do Conselho",
  certidaoNegativa: "Certidão Negativa do Conselho",
  carteiraVacinacao: "Carteira de Vacinação",
  diploma: "Diploma de Graduação",
  carimbo: "Foto do Carimbo"
};

export default function PortalCooperativa() {
  const currentUser = auth.currentUser;
  const [activeTab, setActiveTab] = useState('cadastro'); // 'cadastro' | 'status' | 'reset' | 'validades'
  const [profissionais, setProfissionais] = useState([]);
  const [validadeFilter, setValidadeFilter] = useState('todos'); // 'todos' | 'vencidos' | 'vencendo' | 'vacinas'

  // Fetch Current User Role and Equipe
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEspecialidadeStatus, setFilterEspecialidadeStatus] = useState('');
  const [statusSubTab, setStatusSubTab] = useState('aguardando_rh'); // 'aguardando_rh', 'reprovados_rh', 'aguardando_ti', 'concluidos'

  // Cooperativas Dinamicas
  const [listaCooperativas, setListaCooperativas] = useState([]);
  const [tiposProfissional, setTiposProfissional] = useState(tiposProfissional_DEFAULT);

  // Form State
  const [form, setForm] = useState({
    nome: '',
    dataNascimento: '',
    telefone: '',
    emailCooperado: '',
    cpf: '',
    rg: '',
    conselho: CONSELHOS[1],
    cooperativa: '',
    tipo: tiposProfissional[0],
    isCoringa: false
  });

  const formatBRDate = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, getCollectionName('cooperativas_config')), orderBy('name')), snap => {
      const coops = snap.docs.map(d => d.data().name);
      setListaCooperativas(coops);
      setForm(prev => ({ ...prev, cooperativa: prev.cooperativa || coops[0] || '' }));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    getDoc(doc(db, getCollectionName('config_especialidades'), 'main')).then(snap => {
      if (snap.exists() && snap.data().lista?.length) setTiposProfissional(snap.data().lista);
    }).catch(() => {});
  }, []);
  const [resetSearch, setResetSearch] = useState('');
  const [resetRequests, setResetRequests] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Temporary Form Files
  const [tempFiles, setTempFiles] = useState({
    carteiraConselho: null,
    certidaoNegativa: null,
    carteiraVacinacao: null,
    diploma: null
  });
  const [tempFiles2, setTempFiles2] = useState({
    carteiraConselho: null,
    certidaoNegativa: null,
    carteiraVacinacao: null,
    diploma: null
  });
  const [editingProfDocs, setEditingProfDocs] = useState({});

  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, getCollectionName('profissionais_parceiros')), 
      where('cooperativaUid', '==', currentUser.uid),
      orderBy('criadoEm', 'desc')
    );
    
    const unsub = onSnapshot(q, snap => {
      setProfissionais(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, getCollectionName('solicitacoes_ti')),
      where('cooperativaUid', '==', currentUser.uid),
      orderBy('criadoEm', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setResetRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [currentUser]);

  const checkCpfBlocked = async (cpf) => {
    const q = query(collection(db, getCollectionName('profissionais_parceiros')), where('cpf', '==', cpf), where('statusRH', '==', 'Bloqueado'));
    const snap = await getDocs(q);
    return !snap.empty;
  };

  const validarCPF = (cpf) => {
    cpf = cpf.replace(/[^\d]+/g, '');
    if (cpf === '' || cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
    let add = 0;
    for (let i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i);
    let rev = 11 - (add % 11);
    if (rev === 10 || rev === 11) rev = 0;
    if (rev !== parseInt(cpf.charAt(9))) return false;
    add = 0;
    for (let i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i);
    rev = 11 - (add % 11);
    if (rev === 10 || rev === 11) rev = 0;
    if (rev !== parseInt(cpf.charAt(10))) return false;
    return true;
  };

  const handleTempFileSelect = (e, docType) => {
    const file = e.target.files[0];
    if (file) setTempFiles(prev => ({...prev, [docType]: file}));
  };

  const handleTempFile2Select = (e, docType) => {
    const file = e.target.files[0];
    if (file) setTempFiles2(prev => ({...prev, [docType]: file}));
  };

  const removeTempFile = (docType) => {
    setTempFiles(prev => ({...prev, [docType]: null}));
  };

  const removeTempFile2 = (docType) => {
    setTempFiles2(prev => ({...prev, [docType]: null}));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarCPF(form.cpf)) {
      alert("⚠️ O CPF informado é inválido. Por favor, corrija antes de enviar.");
      return;
    }

    setSubmitting(true);
    try {
      console.log("Iniciando verificação de CPF bloqueado...");
      const isBlocked = await checkCpfBlocked(form.cpf);
      console.log("Verificação de CPF concluída.");
      if (isBlocked) {
        alert("🚨 ATENÇÃO: Este CPF encontra-se bloqueado no sistema e não pode ser cadastrado por nenhuma cooperativa.");
        setSubmitting(false);
        return;
      }

      let profId = editingId;

      if (!editingId) {
        console.log("Criando novo documento no Firestore...");
        const docRef = await addDoc(collection(db, getCollectionName('profissionais_parceiros')), {
          ...form,
          nome: toTitleCase(form.nome),
          cooperativaUid: currentUser.uid,
          cooperativaEmail: currentUser.email,
          statusRH: 'Aguardando',   
          statusTI: 'Aguardando',   
          usuarioSistema: '',
          senhaSistema: '',
          usuarioAulas: '',
          senhaAulas: '',
          documentos: {
            carteiraConselho: null,
            certidaoNegativa: null,
            diploma: null,
            carteiraVacinacao: null,
            carimbo: null
          },
          criadoEm: new Date().toISOString()
        });
        profId = docRef.id;
      } else {
        await updateDoc(doc(db, getCollectionName('profissionais_parceiros'), editingId), {
          ...form,
          nome: toTitleCase(form.nome),
          statusRH: 'Aguardando',
          motivoReprovacao: ''
        });
      }

      console.log("ID do profissional:", profId);
      console.log("Iniciando upload de arquivos...");

      // Agora faz upload dos arquivos temporarios
      const updates = {};
      for (const docType of Object.keys(DOC_TYPES)) {
        const file = tempFiles[docType];
        const file2 = tempFiles2[docType];
        let docUpdate = {};

        if (file) {
          const resultado = await uploadArquivo(file, `profissionais/${profId}`);
          docUpdate = { nome: resultado.nome, url: resultado.url, publicId: resultado.publicId };
          if (editingProfDocs[docType] && editingProfDocs[docType].validade !== undefined) {
            docUpdate.validade = editingProfDocs[docType].validade || null;
          }
        } else if (editingProfDocs[docType]) {
          docUpdate = { 
            nome: editingProfDocs[docType].nome || null, 
            url: editingProfDocs[docType].url || null, 
            path: editingProfDocs[docType].path || null, 
            validade: editingProfDocs[docType].validade || null
          };
        }

        if (file2) {
          const resultado2 = await uploadArquivo(file2, `profissionais/${profId}`);
          docUpdate = { ...docUpdate, nome2: resultado2.nome, url2: resultado2.url, publicId2: resultado2.publicId };
        } else if (editingProfDocs[docType] && editingProfDocs[docType].url2) {
          docUpdate = { 
            ...docUpdate, 
            nome2: editingProfDocs[docType].nome2 || null, 
            url2: editingProfDocs[docType].url2 || null, 
            path2: editingProfDocs[docType].path2 || null 
          };
        }

        if (Object.keys(docUpdate).length > 0) {
          updates[`documentos.${docType}`] = docUpdate;
        }
      }

      console.log("Atualizando Firestore com as URLs dos documentos...");
      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, getCollectionName('profissionais_parceiros'), profId), updates);
      }

      alert(editingId ? 'Cadastro reenviado e documentos atualizados com sucesso!' : 'Cadastro e documentos enviados com sucesso! Aguarde a análise do RH.');
      cancelEdit();
      setActiveTab('status');
    } catch (err) {
      console.error("Erro no handleSubmit:", err);
      alert('Erro ao enviar o cadastro ou documentos: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSolicitarReset = async (p) => {
    if (!window.confirm(`Deseja solicitar o reset de senha para ${p.nome}?`)) return;
    
    try {
      await addDoc(collection(db, getCollectionName('solicitacoes_ti')), {
        profId: p.id,
        profNome: p.nome,
        profCpf: p.cpf,
        cooperativaUid: currentUser.uid,
        cooperativaNome: p.cooperativa,
        status: 'Pendente',
        tipo: 'Reset de Senha',
        criadoEm: new Date().toISOString()
      });
      alert('Solicitação enviada com sucesso para a TI.');
    } catch (err) {
      console.error(err);
      alert('Erro ao enviar solicitação.');
    }
  };

  const handleEdit = (p) => {
    setForm({
      nome: p.nome || '',
      dataNascimento: p.dataNascimento || '',
      telefone: p.telefone || '',
      emailCooperado: p.emailCooperado || '',
      cpf: p.cpf || '',
      rg: p.rg || '',
      conselho: p.conselho || CONSELHOS[1],
      cooperativa: p.cooperativa || listaCooperativas[0] || '',
      tipo: p.tipo || tiposProfissional[0],
      isCoringa: !!p.isCoringa
    });
    setEditingId(p.id);
    let docs = p.documentos;
    if (Array.isArray(docs) || !docs) docs = {};
    setEditingProfDocs(docs);
    setTempFiles({ carteiraConselho: null, certidaoNegativa: null, carteiraVacinacao: null, diploma: null, carimbo: null });
    setTempFiles2({ carteiraConselho: null, certidaoNegativa: null, carteiraVacinacao: null, diploma: null, carimbo: null });
    setActiveTab('cadastro');
  };

  const handleDeleteExistingDoc = async (docType, docItem, fileIndex = 1) => {
    if (!window.confirm(`Deseja excluir permanentemente este arquivo?`)) return;
    
    try {
      // Remove só a referência no cadastro; o arquivo continua no Storage.

      const novosDocs = { ...editingProfDocs };
      const docRef = doc(db, getCollectionName('profissionais_parceiros'), editingId);

      if (fileIndex === 1) {
        novosDocs[docType] = { ...novosDocs[docType], url: null, path: null, nome: null };
        await updateDoc(docRef, { [`documentos.${docType}.url`]: null, [`documentos.${docType}.path`]: null, [`documentos.${docType}.nome`]: null });
      } else {
        novosDocs[docType] = { ...novosDocs[docType], url2: null, path2: null, nome2: null };
        await updateDoc(docRef, { [`documentos.${docType}.url2`]: null, [`documentos.${docType}.path2`]: null, [`documentos.${docType}.nome2`]: null });
      }
      
      setEditingProfDocs(novosDocs);
      alert('Arquivo excluído.');
    } catch (err) {
      console.error(err);
      alert('Erro ao excluir arquivo.');
    }
  };

  const handleBlock = async (p) => {
    const motivo = window.prompt(`🚫 Solicitação de Bloqueio para ${p.nome} (CPF: ${p.cpf}).\nPor favor, informe o motivo do bloqueio:`);
    if (!motivo) return;

    try {
      await updateDoc(doc(db, getCollectionName('profissionais_parceiros'), p.id), {
        statusRH: 'Bloqueado',
        statusTI: 'Bloqueado',
        motivoBloqueio: motivo,
        bloqueadoPor: currentUser.email,
        bloqueadoEm: new Date().toISOString()
      });
      alert('Profissional bloqueado com sucesso. Ele não poderá mais ser escalado.');
    } catch (err) {
      console.error(err);
      alert('Erro ao bloquear profissional.');
    }
  };

  const cancelEdit = () => {
    setForm({
      nome: '', dataNascimento: '', telefone: '', emailCooperado: '', cpf: '', rg: '',
      conselho: CONSELHOS[1], cooperativa: listaCooperativas[0] || '', tipo: tiposProfissional[0], isCoringa: false
    });
    setEditingId(null);
    setEditingProfDocs({});
    setTempFiles({ carteiraConselho: null, certidaoNegativa: null, carteiraVacinacao: null, diploma: null, carimbo: null });
    setTempFiles2({ carteiraConselho: null, certidaoNegativa: null, carteiraVacinacao: null, diploma: null, carimbo: null });
  };

  // KPIs e Filtros
  const listAguardandoRH = profissionais.filter(p => p.statusRH === 'Aguardando' || p.statusRH === 'Pendente Doc');
  const listReprovadosRH = profissionais.filter(p => p.statusRH === 'Reprovado');
  const listAguardandoTI = profissionais.filter(p => p.statusRH === 'Aprovado' && p.statusTI !== 'Criado');
  const listConcluidos = profissionais.filter(p => p.statusRH === 'Aprovado' && p.statusTI === 'Criado');
  const listBloqueados = profissionais.filter(p => p.statusRH === 'Bloqueado');

  const countAguardando = listAguardandoRH.length;
  const countAprovados = listConcluidos.length + listAguardandoTI.length;
  const countRecusados = listReprovadosRH.length + listBloqueados.length;

  const filteredProfs = profissionais.filter(p => p.nome.toLowerCase().includes(searchTerm.toLowerCase()));
  const especialidadesStatus = [...new Set(profissionais.map(p => p.tipo).filter(Boolean))].sort();

  const s = {
    page: { padding: '24px', fontFamily: "'Inter', sans-serif", background: '#f8fafc', minHeight: '100vh', color: '#1e293b' },
    header: { marginBottom: '32px', textAlign: 'center', padding: '0 16px' },
    title: { margin: 0, fontSize: '2.2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.025em' },
    tabs: { display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '2px', flexWrap: 'wrap' },
    tab: (active) => ({ 
      padding: '10px 20px', cursor: 'pointer', fontWeight: 700, fontSize: '0.95rem',
      transition: 'all 0.2s ease',
      borderBottom: active ? '4px solid #4f46e5' : '4px solid transparent', 
      color: active ? '#4f46e5' : '#64748b' 
    }),
    card: { 
      background: '#fff', borderRadius: '16px', padding: '32px', 
      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)', 
      maxWidth: '1200px', margin: '0 auto' 
    },
    input: { 
      width: '100%', padding: '12px 16px', margin: '8px 0 20px', 
      border: '1px solid #cbd5e1', borderRadius: '10px', boxSizing: 'border-box',
      fontSize: '0.95rem', transition: 'border-color 0.2s', outline: 'none'
    },
    btn: { 
      background: '#4f46e5', color: '#fff', border: 'none', padding: '14px 28px', 
      borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '1rem',
      boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.2)', transition: 'transform 0.1s'
    },
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px', marginTop: '16px' },
    th: { textAlign: 'left', padding: '12px 16px', color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
    td: { padding: '16px', background: '#fff', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', fontSize: '0.95rem' },
    badge: (status) => ({ 
      padding: '6px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 800,
      background: status === 'Aprovado' || status === 'Criado' ? '#dcfce7' : status === 'Reprovado' || status === 'Bloqueado' ? '#fee2e2' : '#fef9c3',
      color: status === 'Aprovado' || status === 'Criado' ? '#166534' : status === 'Reprovado' || status === 'Bloqueado' ? '#991b1b' : '#854d0e',
      textTransform: 'uppercase', letterSpacing: '0.025em'
    }),
    kpiContainer: { display: 'flex', gap: '20px', marginBottom: '32px', flexWrap: 'wrap' },
    kpiCard: (type) => ({ 
      flex: '1 1 250px', background: type === 'waiting' ? '#eff6ff' : type === 'approved' ? '#f0fdf4' : '#fef2f2', 
      padding: '24px', borderRadius: '16px', border: '1px solid',
      borderColor: type === 'waiting' ? '#dbeafe' : type === 'approved' ? '#dcfce7' : '#fee2e2',
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
    }),
    kpiValue: { fontSize: '2.5rem', fontWeight: 900, margin: '8px 0 0', color: '#0f172a', lineHeight: 1 },
    kpiLabel: { fontSize: '0.85rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' },
    docSlot: { 
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', 
      padding: '16px', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' 
    }
  };

  const DOC_LABELS_SHORT = {
    carteiraConselho: "Conselho",
    certidaoNegativa: "Cert. Negativa",
    carteiraVacinacao: "Vacinação",
    diploma: "Diploma",
    carimbo: "Carimbo"
  };

  const getValidadeStatus = (validade) => {
    if (!validade) return null;
    const hoje = new Date();
    const dv = new Date(validade + 'T00:00:00');
    const trintaDias = new Date();
    trintaDias.setDate(hoje.getDate() + 30);
    if (dv < hoje) return 'vencido';
    if (dv < trintaDias) return 'vencendo';
    return 'ok';
  };


  const validadeCellStyle = (status) => ({
    padding: '6px 8px', borderRadius: '8px', fontSize: '0.72rem', fontWeight: 700,
    textAlign: 'center', lineHeight: '1.3',
    background: status === 'vencido' ? '#fee2e2' : status === 'vencendo' ? '#fef9c3' : status === 'ok' ? '#f0fdf4' : '#f8fafc',
    color: status === 'vencido' ? '#991b1b' : status === 'vencendo' ? '#854d0e' : status === 'ok' ? '#166534' : '#94a3b8',
    border: `1px solid ${status === 'vencido' ? '#fca5a5' : status === 'vencendo' ? '#fde68a' : status === 'ok' ? '#bbf7d0' : '#e2e8f0'}`
  });

  const renderTable = (lista) => (
    <div style={{ overflowX: 'auto' }}>
    <table style={s.table}>
      <thead>
        <tr>
          <th style={s.th}>Profissional</th>
          {Object.values(DOC_LABELS_SHORT).map(label => (
            <th key={label} style={{...s.th, textAlign: 'center', fontSize: '0.7rem', minWidth: '90px'}}>{label}</th>
          ))}
          <th style={s.th}>Status Geral</th>
          <th style={s.th}>Acesso (TI)</th>
        </tr>
      </thead>
      <tbody>
        {lista.map(p => {
          let pendencia = 'Finalizado';
          if (p.statusRH === 'Bloqueado') pendencia = '❌ BLOQUEADO';
          else if (p.statusRH === 'Reprovado') pendencia = '❌ REPROVADO';
          else if (p.statusRH === 'Aguardando' || p.statusRH === 'Pendente Doc') pendencia = '⏳ COM O RH';
          else if (p.statusTI !== 'Criado') pendencia = '⏳ COM A TI';

          const docsObj = p.documentos && !Array.isArray(p.documentos) ? p.documentos : {};

          return (
            <tr key={p.id}>
              <td style={s.td}>
                <strong>{p.nome} {p.isCoringa && '🃏'}</strong>
                {(() => {
                  const ls = getLiberacaoStatus(p);
                  if (!ls) return null;
                  return (
                    <span style={{marginLeft: '6px', padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 800,
                      background: ls.ativa ? '#f3e8ff' : '#fee2e2',
                      color: ls.ativa ? '#6b21a8' : '#991b1b',
                      border: `1px solid ${ls.ativa ? '#d8b4fe' : '#fca5a5'}`}}>
                      {ls.ativa ? `🟣 Lib.Temp ${ls.diasRestantes}d` : '⚠️ Lib.Temp VENCIDA'}
                    </span>
                  );
                })()}
                <br/>
                <span style={{fontSize: '0.8rem', color: '#64748b'}}>{p.tipo} - CPF: {p.cpf}</span><br/>
                <span style={{fontSize: '0.8rem', color: '#64748b'}}>Coop: {p.cooperativa}</span><br/>
                <div style={{marginTop: '4px', fontSize: '0.75rem', color: '#475569'}}>
                  <strong>Solicitado:</strong> {formatBRDate(p.criadoEm)}
                  {p.aprovadoEm && <div><strong>Aprovado:</strong> {formatBRDate(p.aprovadoEm)}</div>}
                </div>
              </td>
              {Object.keys(DOC_LABELS_SHORT).map(docKey => {
                const docItem = docsObj[docKey];
                const validade = docItem?.validade;
                const status = getValidadeStatus(validade);
                const hasDueDate = docKey !== 'diploma' && docKey !== 'carimbo';
                return (
                  <td key={docKey} style={{...s.td, textAlign: 'center', padding: '8px 4px'}}>
                    {docItem ? (
                      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                        <div style={{display: 'flex', gap: '4px'}}>
                          {docItem.url && <ArquivoLink href={docItem.url} target="_blank" rel="noreferrer" style={{fontSize: '0.7rem', color: '#3b82f6', textDecoration: 'none'}}>📄 1</ArquivoLink>}
                          {docItem.url2 && <ArquivoLink href={docItem.url2} target="_blank" rel="noreferrer" style={{fontSize: '0.7rem', color: '#3b82f6', textDecoration: 'none'}}>📄 2</ArquivoLink>}
                        </div>
                        {hasDueDate ? (
                          validade ? (
                            <div style={{...validadeCellStyle(status), marginTop: '4px'}}>
                              {new Date(validade + 'T00:00:00').toLocaleDateString('pt-BR')}
                              <div style={{fontSize: '0.65rem', marginTop: '2px'}}>
                                {status === 'vencido' ? '❌ Vencido' : status === 'vencendo' ? '⚠️ Vencendo' : '✅ Em dia'}
                              </div>
                            </div>
                          ) : (
                            <div style={{...validadeCellStyle(null), marginTop: '4px'}}>Sem data</div>
                          )
                        ) : (
                          <div style={{...validadeCellStyle('ok'), marginTop: '4px', background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0'}}>Sem validade</div>
                        )}
                      </div>
                    ) : (
                      <div style={{...validadeCellStyle(null)}}>—</div>
                    )}
                  </td>
                );
              })}
              <td style={s.td}>
                <span style={{fontWeight: 700, fontSize: '0.85rem', color: pendencia.includes('RH') || pendencia.includes('TI') ? '#ca8a04' : pendencia.includes('Finalizado') ? '#16a34a' : '#dc2626'}}>{pendencia}</span>
                
                {p.statusRH === 'Reprovado' && p.motivoReprovacao && (
                  <div style={{marginTop: '4px', fontSize: '0.75rem', color: '#991b1b', background: '#fee2e2', padding: '6px', borderRadius: '4px', border: '1px solid #f87171'}}>
                    <strong>Motivo da Recusa:</strong> {p.motivoReprovacao}
                  </div>
                )}

                {p.statusRH === 'Bloqueado' && p.motivoBloqueio && (
                  <div style={{marginTop: '4px', fontSize: '0.75rem', color: '#991b1b', background: '#fee2e2', padding: '6px', borderRadius: '4px', border: '1px solid #f87171'}}>
                    <strong>Motivo do Bloqueio:</strong> {p.motivoBloqueio}
                  </div>
                )}

                {p.statusRH !== 'Bloqueado' && (
                  <button onClick={() => handleEdit(p)} style={{background: 'transparent', border: '1px solid #94a3b8', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer', marginTop: '8px', display: 'block'}}>✏️ Corrigir</button>
                )}
                {p.statusRH !== 'Bloqueado' && pendencia !== 'Finalizado' && (
                  <button onClick={() => handleBlock(p)} style={{background: 'transparent', border: 'none', color: '#991b1b', textDecoration: 'underline', padding: '4px 0', fontSize: '0.75rem', cursor: 'pointer', display: 'block', marginTop: '4px'}}>🚫 Solicitar Bloqueio</button>
                )}
              </td>
              <td style={s.td}>
                {p.statusTI === 'Criado' && p.statusRH !== 'Bloqueado' ? (
                  <div style={{background: '#f0fdf4', padding: '10px', border: '1px solid #bbf7d0', borderRadius: '6px', fontSize: '0.8rem', lineHeight: '1.6'}}>
                    {p.servidorIW && (
                      <div><strong>🖥️ Servidor:</strong> {p.servidorIW}</div>
                    )}
                    <strong>IW:</strong> {p.usuarioSistema} / {p.senhaSistema}<br/>
                    <strong>Aulas:</strong> {p.usuarioAulas} / {p.senhaAulas}
                  </div>
                ) : (
                  <span style={s.badge(p.statusTI)}>{p.statusTI}</span>
                )}
              </td>
            </tr>
          );
        })}
        {lista.length === 0 && (
          <tr><td colSpan={7} style={{...s.td, textAlign: 'center'}}>Nenhum profissional listado.</td></tr>
        )}
      </tbody>
    </table>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>🤝 Portal da Cooperativa</h1>
        <p style={{ color: '#64748b', margin: '4px 0 0' }}>Cadastre e gerencie seus profissionais parceiros.</p>
      </div>

      <div style={s.kpiContainer}>
        <div style={s.kpiCard('waiting')}>
          <div style={s.kpiLabel}>Aguardando / Em Fila</div>
          <div style={s.kpiValue}>{countAguardando}</div>
        </div>
        <div style={s.kpiCard('approved')}>
          <div style={s.kpiLabel}>Aprovados pelo RH</div>
          <div style={{...s.kpiValue, color: '#166534'}}>{countAprovados}</div>
        </div>
        <div style={s.kpiCard('rejected')}>
          <div style={s.kpiLabel}>Recusados / Bloqueados</div>
          <div style={{...s.kpiValue, color: '#991b1b'}}>{countRecusados}</div>
        </div>
      </div>

      <div style={s.tabs}>
        <div style={s.tab(activeTab === 'cadastro')} onClick={() => { setActiveTab('cadastro'); cancelEdit(); }}>{editingId ? '✏️ Editando Cadastro' : '+ Novo Cadastro'}</div>
        <div style={s.tab(activeTab === 'status')} onClick={() => setActiveTab('status')}>Status e Documentos</div>
        <div style={s.tab(activeTab === 'validades')} onClick={() => setActiveTab('validades')}>⚠️ Validade de Documentos</div>
        <div style={s.tab(activeTab === 'reset')} onClick={() => setActiveTab('reset')}>Solicitar Reset de Senha</div>
      </div>

      <div style={s.card}>
        {activeTab === 'cadastro' && (
          <form onSubmit={handleSubmit}>
            <h2 style={{marginTop: 0, color: '#0f172a'}}>{editingId ? 'Corrigir Dados do Profissional' : 'Dados do Profissional'}</h2>
            
            <h3 style={{fontSize: '1rem', color: '#334155', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px'}}>1. Dados Pessoais</h3>
            <div style={{display: 'flex', gap: '16px'}}>
              <div style={{flex: 2}}>
                <label>Nome Completo</label>
                <input required style={s.input} value={form.nome} onChange={e=>setForm({...form, nome: e.target.value})} />
              </div>
              <div style={{flex: 1}}>
                <label>Data de Nascimento</label>
                <input required type="date" style={s.input} value={form.dataNascimento} onChange={e=>setForm({...form, dataNascimento: e.target.value})} />
              </div>
            </div>

            <div style={{display: 'flex', gap: '16px'}}>
              <div style={{flex: 1}}>
                <label>CPF</label>
                <input required style={s.input} placeholder="Apenas números" value={form.cpf} onChange={e=>setForm({...form, cpf: e.target.value.replace(/\D/g, '')})} maxLength={11} />
              </div>
              <div style={{flex: 1}}>
                <label>RG</label>
                <input required style={s.input} value={form.rg} onChange={e=>setForm({...form, rg: e.target.value})} />
              </div>
              <div style={{flex: 1}}>
                <label>Telefone / WhatsApp</label>
                <input required style={s.input} value={form.telefone} onChange={e=>setForm({...form, telefone: e.target.value})} />
              </div>
            </div>

            <div style={{display: 'flex', gap: '16px'}}>
              <div style={{flex: 1}}>
                <label>Email do Cooperado</label>
                <input required type="email" style={s.input} value={form.emailCooperado} onChange={e=>setForm({...form, emailCooperado: e.target.value})} />
              </div>
            </div>

            <h3 style={{fontSize: '1rem', color: '#334155', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginTop: '16px'}}>2. Atuação Profissional</h3>
            
            <div style={{display: 'flex', gap: '16px'}}>
              <div style={{flex: 1}}>
                <label>Cooperativa Vinculada</label>
                <select required style={s.input} value={form.cooperativa} onChange={e=>setForm({...form, cooperativa: e.target.value})}>
                  {listaCooperativas.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{flex: 1}}>
                <label>Tipo de Profissional</label>
                <select required style={s.input} value={form.tipo} onChange={e=>setForm({...form, tipo: e.target.value})}>
                  {tiposProfissional.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div style={{display: 'flex', gap: '16px', alignItems: 'center'}}>
              <div style={{flex: 1}}>
                <label>Conselho</label>
                <select required style={s.input} value={form.conselho} onChange={e=>setForm({...form, conselho: e.target.value})}>
                  {CONSELHOS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{flex: 1, paddingTop: '10px'}}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600, color: '#0f172a' }}>
                  <input type="checkbox" checked={form.isCoringa} onChange={e=>setForm({...form, isCoringa: e.target.checked})} style={{width: '20px', height: '20px'}} />
                  🃏 Este profissional atua como Coringa?
                </label>
              </div>
            </div>

            <h3 style={{fontSize: '1rem', color: '#334155', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginTop: '16px'}}>3. Envio de Documentação</h3>
            
            <div style={{ margin: '16px 0 24px 0', background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <p style={{margin: '0 0 16px 0', fontSize: '0.9rem', color: '#64748b'}}>Anexe os arquivos (PDF ou Imagem) abaixo antes de salvar o cadastro.</p>
              
              {Object.keys(DOC_TYPES).map(docType => {
                const existingDoc = editingProfDocs[docType];
                const tempDoc = tempFiles[docType];

                return (
                  <div key={docType} style={{...s.docSlot, background: '#fff', marginBottom: '8px'}}>
                    <div>
                      <strong style={{display: 'block', color: '#334155', fontSize: '0.95rem'}}>{DOC_TYPES[docType]}</strong>
                      {existingDoc && (existingDoc.url || existingDoc.url2) ? (
                        <span style={{fontSize: '0.8rem', color: '#16a34a', fontWeight: 600}}>✅ Já existe no servidor</span>
                      ) : (tempDoc || tempFiles2[docType]) ? (
                        <span style={{fontSize: '0.8rem', color: '#ca8a04', fontWeight: 600}}>📄 Pronto para envio</span>
                      ) : (
                        <span style={{fontSize: '0.8rem', color: '#dc2626', fontWeight: 600}}>❌ Não anexado</span>
                      )}
                    </div>
                    
                    <div style={{display: 'flex', gap: '8px', alignItems: 'flex-start'}}>
                      {docType !== 'diploma' && docType !== 'carimbo' && (
                      <div style={{marginRight: '16px'}}>
                        <label style={{fontSize: '0.75rem', fontWeight: 800, color: '#64748b', display: 'block'}}>Validade</label>
                        <input 
                          type="date" 
                          style={{...s.input, margin: 0, padding: '4px 8px'}} 
                          value={editingProfDocs[docType]?.validade || ''}
                          onChange={async (e) => {
                            const val = e.target.value;
                            if (editingId) {
                              await updateDoc(doc(db, getCollectionName('profissionais_parceiros'), editingId), {
                                [`documentos.${docType}.validade`]: val
                              });
                            }
                            setEditingProfDocs(prev => ({
                              ...prev,
                              [docType]: { ...(prev[docType] || {}), validade: val }
                            }));
                          }}
                        />
                      </div>
                      )}
                      <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                        <strong style={{fontSize: '0.75rem'}}>Frente / Arq 1</strong>
                        {existingDoc && existingDoc.url ? (
                          <div style={{display: 'flex', gap: '8px'}}>
                            <ArquivoLink href={existingDoc.url} target="_blank" rel="noreferrer" style={{color: '#3b82f6', fontSize: '0.75rem', textDecoration: 'none'}}>Ver</ArquivoLink>
                            <button type="button" onClick={() => handleDeleteExistingDoc(docType, existingDoc, 1)} style={{background: '#fee2e2', color: '#991b1b', border: 'none', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem'}}>Remover</button>
                          </div>
                        ) : tempDoc ? (
                          <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                            <span style={{fontSize: '0.7rem', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{tempDoc.name}</span>
                            <button type="button" onClick={() => removeTempFile(docType)} style={{background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem'}}>Remover</button>
                          </div>
                        ) : (
                          <label style={{ background: '#3b82f6', color: '#fff', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, textAlign: 'center' }}>
                            Anexar Frente
                            <input type="file" style={{ display: 'none' }} onChange={e => handleTempFileSelect(e, docType)} accept=".pdf,image/*" />
                          </label>
                        )}
                      </div>

                      <div style={{display: 'flex', flexDirection: 'column', gap: '4px', marginLeft: '12px'}}>
                        <strong style={{fontSize: '0.75rem'}}>Verso / Arq 2 (Opcional)</strong>
                        {existingDoc && existingDoc.url2 ? (
                          <div style={{display: 'flex', gap: '8px'}}>
                            <ArquivoLink href={existingDoc.url2} target="_blank" rel="noreferrer" style={{color: '#3b82f6', fontSize: '0.75rem', textDecoration: 'none'}}>Ver</ArquivoLink>
                            <button type="button" onClick={() => handleDeleteExistingDoc(docType, existingDoc, 2)} style={{background: '#fee2e2', color: '#991b1b', border: 'none', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem'}}>Remover</button>
                          </div>
                        ) : tempFiles2[docType] ? (
                          <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                            <span style={{fontSize: '0.7rem', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{tempFiles2[docType].name}</span>
                            <button type="button" onClick={() => removeTempFile2(docType)} style={{background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem'}}>Remover</button>
                          </div>
                        ) : (
                          <label style={{ background: '#e2e8f0', color: '#334155', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, textAlign: 'center' }}>
                            Anexar Verso
                            <input type="file" style={{ display: 'none' }} onChange={e => handleTempFile2Select(e, docType)} accept=".pdf,image/*" />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{display: 'flex', gap: '16px'}}>
              <button type="submit" disabled={submitting} style={{...s.btn, flex: 1, padding: '16px', fontSize: '1.1rem'}}>
                {submitting ? '⏳ Fazendo Upload e Salvando...' : editingId ? 'Salvar Alterações e Reenviar ao RH' : '📤 Enviar Cadastro e Documentos'}
              </button>
              {editingId && (
                <button type="button" onClick={cancelEdit} style={{...s.btn, background: '#94a3b8', flex: 0.3}}>Cancelar</button>
              )}
            </div>
          </form>
        )}

        {activeTab === 'status' && (
          <div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
              <h2 style={{margin: 0, flex: 'none'}}>Pesquisa e Status</h2>
              <input
                style={{ flex: '1 1 200px', padding: '9px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', outline: 'none' }}
                placeholder="🔍 Nome do profissional..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <select
                value={filterEspecialidadeStatus}
                onChange={e => setFilterEspecialidadeStatus(e.target.value)}
                style={{ flex: '1 1 180px', padding: '9px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', outline: 'none', background: '#fff' }}
              >
                <option value="">Todas especialidades</option>
                {especialidadesStatus.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
              {(searchTerm || filterEspecialidadeStatus) && (
                <button
                  onClick={() => { setSearchTerm(''); setFilterEspecialidadeStatus(''); }}
                  style={{ padding: '9px 14px', borderRadius: '8px', border: '1px solid #fca5a5', background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', flex: 'none' }}
                >
                  ✕ Limpar
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', background: '#fff', padding: '6px', borderRadius: '14px', border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
              <button 
                onClick={() => setStatusSubTab('aguardando_rh')} 
                style={{ flex: '1 1 150px', padding: '12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: '0.7rem', background: statusSubTab === 'aguardando_rh' ? '#eff6ff' : 'transparent', color: statusSubTab === 'aguardando_rh' ? '#2563eb' : '#64748b', transition: 'all 0.2s' }}
              >
                ⏳ AGUARDANDO RH ({listAguardandoRH.length})
              </button>
              <button 
                onClick={() => setStatusSubTab('reprovados_rh')} 
                style={{ flex: '1 1 150px', padding: '12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: '0.7rem', background: statusSubTab === 'reprovados_rh' ? '#fef2f2' : 'transparent', color: statusSubTab === 'reprovados_rh' ? '#dc2626' : '#64748b', transition: 'all 0.2s' }}
              >
                ❌ REPROVADOS RH ({listReprovadosRH.length})
              </button>
              <button 
                onClick={() => setStatusSubTab('aguardando_ti')} 
                style={{ flex: '1 1 150px', padding: '12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: '0.7rem', background: statusSubTab === 'aguardando_ti' ? '#fefce8' : 'transparent', color: statusSubTab === 'aguardando_ti' ? '#ca8a04' : '#64748b', transition: 'all 0.2s' }}
              >
                ⚙️ AGUARDANDO TI ({listAguardandoTI.length})
              </button>
              <button 
                onClick={() => setStatusSubTab('concluidos')} 
                style={{ flex: '1 1 150px', padding: '12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: '0.7rem', background: statusSubTab === 'concluidos' ? '#f0fdf4' : 'transparent', color: statusSubTab === 'concluidos' ? '#166534' : '#64748b', transition: 'all 0.2s' }}
              >
                ✅ CONCLUÍDOS ({listConcluidos.length})
              </button>
              <button 
                onClick={() => setStatusSubTab('bloqueados')} 
                style={{ flex: '1 1 150px', padding: '12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: '0.7rem', background: statusSubTab === 'bloqueados' ? '#f1f5f9' : 'transparent', color: statusSubTab === 'bloqueados' ? '#1e293b' : '#64748b', transition: 'all 0.2s' }}
              >
                🚫 BLOQUEADOS ({listBloqueados.length})
              </button>
            </div>

            {loading ? <p>Carregando...</p> : renderTable(
              (statusSubTab === 'aguardando_rh' ? listAguardandoRH :
               statusSubTab === 'reprovados_rh' ? listReprovadosRH :
               statusSubTab === 'aguardando_ti' ? listAguardandoTI :
               statusSubTab === 'concluidos' ? listConcluidos : listBloqueados)
              .filter(p =>
                (!searchTerm || p.nome.toLowerCase().includes(searchTerm.toLowerCase())) &&
                (!filterEspecialidadeStatus || p.tipo === filterEspecialidadeStatus)
              )
            )}
          </div>
        )}

        {activeTab === 'reset' && (
          <div>
            <h2 style={{marginTop: 0}}>Solicitar Reset de Senha</h2>
            <div style={{display: 'flex', gap: '16px', marginBottom: '24px'}}>
              <input 
                style={{...s.input, flex: 1, margin: 0}} 
                placeholder="🔍 Buscar profissional para reset..." 
                value={resetSearch}
                onChange={e => setResetSearch(e.target.value)}
              />
            </div>

            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Profissional</th>
                  <th style={s.th}>Ações</th>
                  <th style={s.th}>Histórico de Solicitações</th>
                </tr>
              </thead>
              <tbody>
                {profissionais
                  .filter(p => p.nome.toLowerCase().includes(resetSearch.toLowerCase()) && p.statusTI === 'Criado')
                  .map(p => (
                    <tr key={p.id}>
                      <td style={s.td}>
                        <strong>{p.nome}</strong><br/>
                        <span style={{fontSize: '0.8rem', color: '#64748b'}}>CPF: {p.cpf}</span>
                      </td>
                      <td style={s.td}>
                        <button style={s.btn} onClick={() => handleSolicitarReset(p)}>🚀 Solicitar Reset</button>
                      </td>
                      <td style={s.td}>
                        {resetRequests.filter(r => r.profId === p.id).map(r => (
                          <div key={r.id} style={{fontSize: '0.75rem', marginBottom: '4px', padding: '4px', background: '#f8fafc', borderRadius: '4px'}}>
                            {new Date(r.criadoEm).toLocaleDateString('pt-BR')} - <strong>{RESET_STATUS[r.status] || r.status}</strong>
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'validades' && renderValidades()}
      </div>
    </div>
  );

  function renderValidades() {
    const hoje = new Date();
    const trintaDias = new Date();
    trintaDias.setDate(hoje.getDate() + 30);

    const profsComDocs = profissionais.filter(p => p.documentos && !Array.isArray(p.documentos));
    
    const filteredProfs = profsComDocs.filter(p => {
      const docs = p.documentos;
      const docKeys = Object.keys(DOC_LABELS_SHORT).filter(k => k !== 'diploma');
      if (validadeFilter === 'todos') return docKeys.some(k => docs[k]?.validade);
      if (validadeFilter === 'vencidos') return docKeys.some(k => { const v = docs[k]?.validade; return v && new Date(v + 'T00:00:00') < hoje; });
      if (validadeFilter === 'vencendo') return docKeys.some(k => { const v = docs[k]?.validade; return v && new Date(v + 'T00:00:00') >= hoje && new Date(v + 'T00:00:00') < trintaDias; });
      if (validadeFilter === 'vacinas') return !!docs.carteiraVacinacao?.validade;
      return false;
    });

    return (
      <div>
        <div style={{display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap'}}>
          <button style={{...s.btn, background: validadeFilter === 'todos' ? '#4f46e5' : '#fff', color: validadeFilter === 'todos' ? '#fff' : '#64748b', border: '1px solid #e2e8f0'}} onClick={() => setValidadeFilter('todos')}>Todos</button>
          <button style={{...s.btn, background: validadeFilter === 'vencidos' ? '#ef4444' : '#fff', color: validadeFilter === 'vencidos' ? '#fff' : '#64748b', border: '1px solid #e2e8f0'}} onClick={() => setValidadeFilter('vencidos')}>🔴 Vencidos</button>
          <button style={{...s.btn, background: validadeFilter === 'vencendo' ? '#f59e0b' : '#fff', color: validadeFilter === 'vencendo' ? '#fff' : '#64748b', border: '1px solid #e2e8f0'}} onClick={() => setValidadeFilter('vencendo')}>🟡 Vencendo</button>
          <button style={{...s.btn, background: validadeFilter === 'vacinas' ? '#10b981' : '#fff', color: validadeFilter === 'vacinas' ? '#fff' : '#64748b', border: '1px solid #e2e8f0'}} onClick={() => setValidadeFilter('vacinas')}>💉 Vacinas</button>
        </div>

        <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Profissional</th>
              <th style={s.th}>Status</th>
              {Object.values(DOC_LABELS_SHORT).map(label => (
                <th key={label} style={{...s.th, textAlign: 'center', fontSize: '0.7rem', minWidth: '100px'}}>{label}</th>
              ))}
              <th style={s.th}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredProfs.map(p => {
              const docs = p.documentos;
              return (
                <tr key={p.id}>
                  <td style={s.td}>
                    <strong>{p.nome}</strong><br/>
                    <span style={{fontSize: '0.78rem', color: '#64748b'}}>{p.tipo}</span>
                  </td>
                  <td style={s.td}><span style={s.badge(p.statusRH)}>{p.statusRH}</span></td>
                  {Object.keys(DOC_LABELS_SHORT).map(docKey => {
                    const validade = docs[docKey]?.validade;
                    const status = getValidadeStatus(validade);
                    const hasDueDate = docKey !== 'diploma';
                    return (
                      <td key={docKey} style={{...s.td, textAlign: 'center', padding: '8px 4px'}}>
                        {hasDueDate ? (
                          validade ? (
                            <div style={validadeCellStyle(status)}>
                              {new Date(validade + 'T00:00:00').toLocaleDateString('pt-BR')}
                              <div style={{fontSize: '0.65rem', marginTop: '2px'}}>
                                {status === 'vencido' ? '❌ Vencido' : status === 'vencendo' ? '⚠️ Vencendo' : '✅ Em dia'}
                              </div>
                            </div>
                          ) : (
                            <div style={{...validadeCellStyle(null)}}>—</div>
                          )
                        ) : (
                          <div style={{...validadeCellStyle('ok'), background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0'}}>Sem validade</div>
                        )}
                      </td>
                    );
                  })}
                  <td style={s.td}>
                    <button onClick={() => handleEdit(p)} style={{width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600}}>
                      ✏️ Atualizar
                    </button>
                  </td>
                </tr>
              );
            })}
            {filteredProfs.length === 0 && (
              <tr><td colSpan={7} style={{...s.td, textAlign: 'center', padding: '40px', color: '#94a3b8'}}>Nenhum documento com validade encontrado.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    );
  }
}
